import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { TrendingUp } from 'lucide-react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ThroughputPoint } from '../api/demands.api';
import { formatDurationRich } from '../utils/format-duration';

interface Props {
  data: ThroughputPoint[];
  isLoading: boolean;
}

/** Converte "2026-09-14" (UTC) para "14/09" para exibir no eixo X. */
function formatShortDate(iso: string): string {
  // iso já é YYYY-MM-DD UTC — evitar new Date() para não ter drift de fuso.
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

/**
 * Gráfico composto: barras = ciclos fechados/dia, linha = tempo médio (em horas).
 * Dois eixos Y independentes para que a linha não "amasse" as barras.
 */
export function ThroughputChart({ data, isLoading }: Props) {
  const chartData = data.map((p) => ({
    date: formatShortDate(p.date),
    fullDate: p.date,
    closedCount: p.closedCount,
    // Convertido para horas — a maioria dos ciclos operacionais fica em horas,
    // ler "38h" é mais rápido do que "138_600_000 ms" no tooltip.
    averageCycleHours: p.averageCycleMs > 0 ? p.averageCycleMs / 3_600_000 : 0,
    averageCycleMs: p.averageCycleMs,
  }));

  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{
      value: number;
      dataKey: string;
      payload: (typeof chartData)[number];
    }>;
  }) => {
    if (!active || !payload || payload.length === 0) return null;
    const point = payload[0].payload;
    return (
      <div className="rounded-lg border border-border bg-card p-3 shadow-lg">
        <p className="mb-1 text-sm font-medium text-foreground">{point.date}</p>
        <p className="text-sm text-primary">
          Concluídas: <span className="font-semibold">{point.closedCount}</span>
        </p>
        {point.averageCycleMs > 0 && (
          <p className="text-sm text-amber-600 dark:text-amber-400">
            Média: {formatDurationRich(point.averageCycleMs)}
          </p>
        )}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Throughput diário
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex h-[300px] items-center justify-center">
            <p className="text-sm text-muted-foreground">Carregando…</p>
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex h-[300px] items-center justify-center">
            <p className="text-sm text-muted-foreground">
              Sem ciclos fechados no período selecionado.
            </p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis
                yAxisId="left"
                allowDecimals={false}
                tick={{ fontSize: 12 }}
                label={{
                  value: 'Concluídas',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 12, fill: 'currentColor' },
                }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 12 }}
                tickFormatter={(v: number) => `${v.toFixed(0)}h`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar
                yAxisId="left"
                dataKey="closedCount"
                name="Concluídas no dia"
                fill="#3b82f6"
                radius={[4, 4, 0, 0]}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="averageCycleHours"
                name="Tempo médio (h)"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
